import * as React from 'react';
import axios from 'src/utils/axios';

const AppContext = React.createContext();

const TOKEN_KEY = 'ah_token';

function AppProvider({ children }) {
	const [settings, setSettingsValues] = React.useState(JSON.parse(localStorage.getItem('settings')));
	const [user, setUserValues] = React.useState(null);
	const [isLoaded, setIsLoaded] = React.useState(false);

	const setSettings = (val) => {
		localStorage.setItem('settings', JSON.stringify(val));
		setSettingsValues(val);
	};

	const clearSession = () => {
		localStorage.removeItem(TOKEN_KEY);
		delete axios.defaults.headers.common.Authorization;
		setUserValues(null);
	};

	// Validates the stored token with the API (page load / after sign-in).
	const updateCurrentUser = React.useCallback(async () => {
		const token = localStorage.getItem(TOKEN_KEY);
		if (!token) {
			setUserValues(null);
			setIsLoaded(true);
			return;
		}
		axios.defaults.headers.common.Authorization = token;
		try {
			const res = await axios.get('/auth/me');
			setUserValues({ ...res.data.user, username: res.data.user.email, token });
		} catch (err) {
			// Only a rejected token logs you out; a network blip keeps the session.
			if (err && typeof err === 'object' && /^auth\//.test(err.message || '')) clearSession();
		}
		setIsLoaded(true);
	}, []);

	React.useEffect(() => {
		updateCurrentUser();
	}, [updateCurrentUser]);

	// Signs in against the self-hosted API. Throws an Error with a human-readable message.
	const login = async (email, password) => {
		try {
			const res = await axios.post('/auth/login', { email, password });
			localStorage.setItem(TOKEN_KEY, res.data.token);
			await updateCurrentUser();
		} catch (err) {
			const code = err && err.message;
			if (code === 'auth/user-not-found' || code === 'auth/wrong-password') throw new Error('Incorrect email or password.');
			if (typeof err === 'string') throw new Error('Cannot reach the server. Please try again.');
			throw new Error('Sign in failed. Please try again.');
		}
	};

	const isAuthenticated = !!(user && user.username);
	const value = {
		isAuthenticated,
		settings,
		setSettings,
		isLoaded,
		user,
		login,
		logout: clearSession,
		updateCurrentUser
	};
	return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
function useApp() {
	const context = React.useContext(AppContext);
	if (context === undefined) {
		throw new Error('useApp must be used within a AppProvider');
	}
	return context;
}
export { AppProvider, useApp };
